import qs from 'qs';
import axios, { AxiosRequestConfig } from 'axios';
import ApiBaseUrl from '../../constants/api/apiBaseURL';
import ManagementApiResources from '../../constants/api/resources/management';
import AdminApiResources from '../../constants/api/resources/wizard';
import { ApiScope } from '../../constants/apiScope';
import { ApiTokenCodes } from '../../constants/apiResponseCodes';
import { TokenService } from './storage.service';
import NotificationsService from './notifications.service';

const axiosInstance = axios.create({
  baseURL: ApiBaseUrl,
  timeout: 60 * 1000,
  withCredentials: true,
  transformResponse: [
    function(data) {
      return data;
    },
  ],
});

function getScope(url: string = ''): ApiScope {
  url = url.replace(ApiBaseUrl, '');
  const parts = url.split('/');
  return parts[0] as ApiScope;
}

function isSecurityScope(url: string = ''): boolean {
  url = url.replace(ApiBaseUrl, '');
  const parts = url.split('/');
  return (parts[1] as string) === 'security_mfa' && (parts[2] as string) !== 'index';
}

axiosInstance.interceptors.request.use(config => {
  const scope = getScope(config.url);
  let token = null;
  if (scope === ApiScope.Management) {
    if (TokenService.hasScopeData(ApiScope.ImpersonateSession)) {
      token = TokenService.getToken(ApiScope.ImpersonateSession);
    } else if (TokenService.hasScopeData(ApiScope.MFALoginManager)) {
      token = TokenService.getToken(ApiScope.MFALoginManager);
    } else if (TokenService.hasScopeData(ApiScope.ManagerSecurity) && isSecurityScope(config.url)) {
      token = TokenService.getToken(ApiScope.ManagerSecurity);
    } else {
      token = TokenService.getToken(ApiScope.Manager);
    }
  } else if (scope === ApiScope.Wizard) {
    if (TokenService.hasScopeData(ApiScope.MFALoginAdmin)) {
      token = TokenService.getToken(ApiScope.MFALoginAdmin);
    } else if (TokenService.hasScopeData(ApiScope.AdminSecurity) && isSecurityScope(config.url)) {
      token = TokenService.getToken(ApiScope.AdminSecurity);
    } else {
      token = TokenService.getToken(ApiScope.Admin);
    }
  }
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  config.paramsSerializer = params => {
    return qs.stringify(params, { arrayFormat: 'brackets' });
  };
  return config;
});

axiosInstance.interceptors.response.use(
  response => {
    if (response.data && (!response.config.responseType || response.config.responseType === 'json')) {
      response.data = JSON.parse(response.data);
    }
    if (response.data && response.data.message) {
      NotificationsService.setMessage(response.data.message, response.data.success);
    }
    return response;
  },
  error => {
    let scope = getScope(error.config.url);
    NotificationsService.setError(error);
    const status = error.response ? error.response.status : null;
    if (status === 401) {
      if (error.response.data) {
        try {
          const data = JSON.parse(error.response.data) ? JSON.parse(error.response.data) : null;
          if (data && data.code) {
            if (
              data.code === ApiTokenCodes.TokenRevoked ||
              data.code === ApiTokenCodes.TokenUnknown ||
              data.code === ApiTokenCodes.InvalidScope
            ) {
              TokenService.removeScopeData(scope);
              location.reload();
              return Promise.reject(error);
            }
            if (data.code !== ApiTokenCodes.TokenExpired) {
              return Promise.reject(error);
            }
          }
        } catch (e) {
          return Promise.reject(error);
        }
      }
      if (error.config && !error.config.__isRetryRequest) {
        if (scope !== ApiScope.Wizard && TokenService.hasScopeData(ApiScope.ImpersonateSession)) {
          scope = ApiScope.ImpersonateSession;
        } else if (scope === ApiScope.Management && !TokenService.hasScopeData(ApiScope.ImpersonateSession)) {
          scope = ApiScope.Manager;
        } else if (scope === ApiScope.Wizard) {
          scope = ApiScope.Admin;
        }
        const refreshToken = TokenService.getRefreshToken(scope);
        if (!refreshToken) {
          if (scope === ApiScope.Manager) {
            if (TokenService.hasScopeData(ApiScope.MFALoginManager)) {
              TokenService.removeScopeData(ApiScope.MFALoginManager);
            } else if (TokenService.hasScopeData(ApiScope.ManagerSecurity)) {
              TokenService.removeScopeData(ApiScope.ManagerSecurity);
            }
          } else if (scope === ApiScope.Admin) {
            if (TokenService.hasScopeData(ApiScope.MFALoginAdmin)) {
              TokenService.removeScopeData(ApiScope.MFALoginAdmin);
            } else if (TokenService.hasScopeData(ApiScope.AdminSecurity)) {
              TokenService.removeScopeData(ApiScope.AdminSecurity);
            }
          }
          TokenService.removeScopeData(scope);
          location.reload();
          return Promise.reject(error);
        }
        const data = {
          refresh_token: refreshToken,
        };
        let url;
        switch (scope) {
          case ApiScope.Admin:
            url = AdminApiResources.refreshAdminToken;
            break;
          case ApiScope.Manager:
            url = ManagementApiResources.refreshManagerToken;
            break;
          case ApiScope.ImpersonateSession:
            url = ManagementApiResources.refreshImpersonateToken;
            break;
          default:
            url = ManagementApiResources.refreshManagerToken;
        }
        return axiosInstance.post(url, data).then(
          ({ data: { access_token, refresh_token } }) => {
            if (access_token) {
              TokenService.saveToken(scope, access_token);
            }
            if (refresh_token) {
              TokenService.saveRefreshToken(scope, refresh_token);
            }
            error.config.__isRetryRequest = true;
            error.config.params = { ...error.config.params, ...{ access_token: access_token } };
            return axiosInstance.request(error.config);
          },
          refreshError => Promise.reject(refreshError),
        );
      }
    }
    return Promise.reject(error);
  },
);

const ApiService = {
  removeHeader() {
    axiosInstance.defaults.headers.common = {};
  },

  get(resource: any, config?: AxiosRequestConfig) {
    return axiosInstance.get(resource, config);
  },

  post(resource: any, data?: any, config?: AxiosRequestConfig) {
    return axiosInstance.post(resource, data, config);
  },

  put(resource: any, data: any, config?: AxiosRequestConfig) {
    return axiosInstance.put(resource, data, config);
  },

  patch(resource: any, data: any, config?: AxiosRequestConfig) {
    return axiosInstance.patch(resource, data, config);
  },

  delete(resource: any, config?: AxiosRequestConfig) {
    return axiosInstance.delete(resource, config);
  },

  customRequest(data: any) {
    return axiosInstance(data);
  },
};

export default ApiService;
